import React, { useState } from 'react';
import { useErrorLogs, type ErrorLog } from '@/hooks/useErrorLogs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Eye, ChevronDown } from 'lucide-react';
import ScrollArea from '@/components/ui/shared/ScrollArea';

const SOURCE_OPTIONS = [
  { value: 'all', label: 'Todas as origens' },
  { value: 'processamento-ia', label: 'Processamento IA' },
];

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function StatusBadge({ status }: { status: number | null }) {
  if (!status) return <span className="text-neutral-500">-</span>;

  if (status >= 200 && status < 300) {
    return <Badge className="bg-green-900/30 text-green-400 border-green-800">{status}</Badge>;
  }
  if (status >= 400 && status < 500) {
    return <Badge className="bg-yellow-900/30 text-yellow-400 border-yellow-800">{status}</Badge>;
  }
  return <Badge className="bg-red-900/30 text-red-400 border-red-800">{status}</Badge>;
}

const LogsTab: React.FC = () => {
  const { logs, loading, hasMore, sourceFilter, fetchLogs, loadMore, changeSourceFilter } = useErrorLogs();
  const [selectedLog, setSelectedLog] = useState<ErrorLog | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-neutral-50">Logs de Erros</h2>
          <p className="text-sm text-neutral-400">Erros registrados nas integrações da conta</p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={sourceFilter || 'all'}
            onValueChange={(val) => changeSourceFilter(val === 'all' ? null : val)}
          >
            <SelectTrigger className="w-[200px] bg-neutral-600 border-[#3F3F46] text-neutral-50">
              <SelectValue placeholder="Filtrar por origem" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181B] border-[#3F3F464D]">
              {SOURCE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLogs}
            disabled={loading}
            className="border-[#3F3F464D] text-neutral-300 hover:text-neutral-50"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="border border-[#3F3F464D] rounded-lg bg-[#18181B]">
        {loading && logs.length === 0 ? (
          <div className="p-8 text-center text-neutral-400">Carregando...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-neutral-400">Nenhum erro registrado</div>
        ) : (
          <>
            <ScrollArea classNames={{ root: 'max-h-[500px] overflow-auto' }}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#18181B] z-10">
                  <tr className="border-b border-[#3F3F464D] text-neutral-400 text-left">
                    <th className="px-4 py-3 font-medium w-[155px]">Data/Hora</th>
                    <th className="px-4 py-3 font-medium w-[140px]">Origem</th>
                    <th className="px-4 py-3 font-medium w-[200px]">Código</th>
                    <th className="px-4 py-3 font-medium">Mensagem</th>
                    <th className="px-4 py-3 font-medium w-[70px] text-center">Status</th>
                    <th className="px-4 py-3 font-medium w-[60px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      className="border-b border-[#3F3F464D] hover:bg-neutral-800/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-neutral-300 text-xs whitespace-nowrap">
                        {formatDate(log.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="default2" className="text-xs">
                          {log.source}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-neutral-200 font-mono text-xs">
                        {log.error_code || '-'}
                      </td>
                      <td className="px-4 py-3 text-neutral-300 truncate max-w-[300px]">
                        {log.error_message}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge status={log.http_status} />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="flex justify-center items-center rounded-[8px] border border-[#3F3F464D] bg-neutral-600 w-[30px] h-[30px] hover:bg-neutral-500 transition-colors"
                        >
                          <Eye size={14} className="text-neutral-200" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>

            {hasMore && (
              <div className="p-3 text-center border-t border-[#3F3F464D]">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadMore}
                  disabled={loading}
                  className="text-neutral-400 hover:text-neutral-50"
                >
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Carregar mais
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={!!selectedLog} onOpenChange={(open) => { if (!open) setSelectedLog(null); }}>
        <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col max-w-[600px]">
          <ScrollArea classNames={{ root: 'max-h-[80dvh] p-5 overflow-auto' }}>
            <DialogHeader>
              <DialogTitle className="text-white">Detalhes do Erro</DialogTitle>
            </DialogHeader>

            {selectedLog && (
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-neutral-500 uppercase">Data/Hora</label>
                    <p className="text-neutral-200 text-sm">{formatDate(selectedLog.created_at)}</p>
                  </div>
                  <div>
                    <label className="text-xs text-neutral-500 uppercase">Origem</label>
                    <p className="text-neutral-200 text-sm">{selectedLog.source}</p>
                  </div>
                  <div>
                    <label className="text-xs text-neutral-500 uppercase">Código</label>
                    <p className="text-neutral-200 text-sm font-mono">{selectedLog.error_code || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-neutral-500 uppercase">Status HTTP</label>
                    <div className="mt-1"><StatusBadge status={selectedLog.http_status} /></div>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-neutral-500 uppercase">Mensagem</label>
                  <p className="text-neutral-200 text-sm mt-1">{selectedLog.error_message}</p>
                </div>

                {selectedLog.details && (
                  <div>
                    <label className="text-xs text-neutral-500 uppercase">Detalhes</label>
                    <pre className="mt-1 text-xs text-neutral-300 bg-neutral-900 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">
                      {selectedLog.details}
                    </pre>
                  </div>
                )}

                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <div>
                    <label className="text-xs text-neutral-500 uppercase">Metadata</label>
                    <pre className="mt-1 text-xs text-neutral-300 bg-neutral-900 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LogsTab;
